import React, { ReactElement, ReactNode } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { mq } from '@hangar31/rc-layout';
import Tippy from '@tippyjs/react';

// (100% -144rem) / 2 = 32
// (var(--vw) - 100%) = 286

// (var(--vw) - 144rem) / 2 = 180

export const SectionStyled = styled.section(
  () => css`
    position: relative;

    grid-column: main-start / main-end;
    width: 100%;
    margin: 0 auto;
    padding-bottom: 20vh;
  `,
  () =>
    mq({
      paddingLeft: ['1.6rem', undefined, '2.4rem', '3.2rem'],
      paddingRight: ['1.6rem', undefined, '2.4rem', '3.2rem'],
    })
);

export const MainStyled = styled.main<{ grid?: boolean }>(
  () => css`
    position: relative;

    display: grid;
    grid-template-columns: auto;
    width: 100%;
  `,
  ({ grid }) =>
    grid &&
    css`
      position: relative;

      grid-template-columns: auto [main-start] auto 1fr [main-end];
      > * {
        grid-row: 1;
      }
    `
);

export const GradientBackground = styled.div(
  ({ theme }) => css`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: -1;

    display: block;

    background: ${theme.gradients.main};

    pointer-events: none;
    content: '';
  `
);

/**
 * Tooltip adapter. Was an empty `styled(react-tippy Tooltip)()` pass-through;
 * react-tippy is unmaintained (React-19 unsafe), so this maps its call-site API
 * onto @tippyjs/react: `title`/`html` -> `content`, `position` -> `placement`.
 * Tippy needs a single ref-holding child, so children are wrapped in a span that
 * also receives the Emotion `css` prop (delivered as `className`). `size`
 * (react-tippy legacy) is accepted for call-site compatibility but unused —
 * tippy.js has no size prop.
 */
export type TooltipStyledProps = React.PropsWithChildren<{
  title?: ReactNode;
  html?: ReactNode;
  position?: 'top' | 'top-start' | 'top-end' | 'bottom' | 'left' | 'right';
  followCursor?: boolean;
  delay?: number | [number | null, number | null];
  distance?: number;
  size?: string;
  className?: string;
}>;

export function TooltipStyled({
  children,
  className,
  delay,
  distance,
  followCursor = false,
  html,
  position = 'top',
  title,
}: TooltipStyledProps): ReactElement {
  return (
    <Tippy
      content={html ?? title}
      delay={delay}
      followCursor={followCursor}
      offset={distance != null ? [0, distance] : undefined}
      placement={position}
    >
      <span className={className}>{children}</span>
    </Tippy>
  );
}
